import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe } from "lucide-react";
import { toast } from "sonner";
import { ALLOWED_LOCALES, LOCALE_LABELS, formatDate, formatTime, formatCurrency, getCurrencyForLocale } from "@/lib/locale";

/**
 * LocaleCard — shared Locale & region picker.
 *
 * Two modes:
 *   - scope="org":  PATCH /api/orgs/me  (org-admin only, default the brief targets)
 *   - scope="user": PATCH /api/users/me (per-user override — null clears it)
 *
 * Both modes share the same visual layout + the same live-preview panel so
 * the user can SEE the impact of a locale change before saving. The preview
 * mirrors what `useLocale()` will render across the app.
 *
 * On save we re-fetch /api/auth/me via AuthContext.refresh() so the
 * LocaleContext hydrates with the new effective_locale immediately — no page
 * reload needed (one of the brief's hard acceptance criteria).
 */
export default function LocaleCard({ scope = "org", initialLocale = null, allowInherit = false, onSaved }) {
  const { locale: currentEffective } = useLocale();
  const { refresh } = useAuth();
  const [value, setValue] = useState(() => {
    if (allowInherit && (initialLocale == null || initialLocale === "")) return "__inherit__";
    return initialLocale || currentEffective || "en-GB";
  });
  const [saving, setSaving] = useState(false);
  // Phase C3 — Track A — server-rendered preview. Pulls
  // `/api/orgs/me/locale-preview?locale=<v>` so the operator sees EXACTLY
  // what outgoing emails / PDFs / CSVs will render — not just the
  // browser's `Intl.*` output. Mirrors the Babel call-site server-side.
  const [serverPreview, setServerPreview] = useState(null);
  const [serverPreviewLoading, setServerPreviewLoading] = useState(false);

  // Keep local state in sync if the parent re-renders with a different initial.
  useEffect(() => {
    if (allowInherit && (initialLocale == null || initialLocale === "")) {
      setValue("__inherit__");
    } else if (initialLocale) {
      setValue(initialLocale);
    }
  }, [initialLocale, allowInherit]);

  // For the preview row, fall through "__inherit__" to the current effective.
  const previewLocale = value === "__inherit__" ? currentEffective : value;
  const sampleDate = new Date();
  const sampleCurrency = getCurrencyForLocale(previewLocale);

  useEffect(() => {
    let cancelled = false;
    setServerPreviewLoading(true);
    api.get("/orgs/me/locale-preview", { params: { locale: previewLocale } })
      .then((r) => { if (!cancelled) setServerPreview(r.data); })
      .catch(() => { if (!cancelled) setServerPreview(null); })
      .finally(() => { if (!cancelled) setServerPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [previewLocale]);

  const save = async () => {
    setSaving(true);
    try {
      if (scope === "org") {
        await api.patch("/orgs/me", { locale: value });
      } else if (value === "__inherit__") {
        await api.patch("/users/me", { clear_locale: true });
      } else {
        await api.patch("/users/me", { locale: value });
      }
      // Re-hydrate AuthContext → LocaleContext picks up the new effective_locale.
      await refresh();
      toast.success("Locale updated");
      onSaved?.(value);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="eb-card p-6" data-testid={`locale-card-${scope}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-navy-50 dark:bg-navy-900 flex items-center justify-center">
          <Globe className="w-5 h-5 text-navy-700 dark:text-navy-300" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-navy-900 dark:text-white">
            Locale &amp; region
          </h3>
          <p className="text-xs text-navy-500 mt-0.5">
            {scope === "org"
              ? "Sets the default date, time and currency formats across this organisation. Individual users can override this from their profile."
              : "Override your organisation's default. Choose “Use organisation default” to inherit."}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <label className="text-xs uppercase tracking-wider text-navy-500 font-semibold block">
          Language &amp; region
        </label>
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger
            className="w-full max-w-md"
            data-testid={`locale-select-trigger-${scope}`}
          >
            <SelectValue placeholder="Choose a locale" />
          </SelectTrigger>
          <SelectContent>
            {allowInherit && (
              <SelectItem value="__inherit__" data-testid="locale-option-inherit">
                Use organisation default
              </SelectItem>
            )}
            {ALLOWED_LOCALES.map((loc) => (
              <SelectItem key={loc} value={loc} data-testid={`locale-option-${loc}`}>
                {LOCALE_LABELS[loc]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div
          className="mt-4 p-4 bg-navy-50/60 dark:bg-navy-900/40 rounded-lg space-y-1.5 text-sm"
          data-testid={`locale-preview-${scope}`}
        >
          <div className="text-[10px] uppercase tracking-wider text-navy-500 font-semibold mb-2">
            Live preview
          </div>
          <div className="flex items-center justify-between">
            <span className="text-navy-500">Date</span>
            <span className="font-mono font-medium" data-testid="locale-preview-date">
              {formatDate(sampleDate, previewLocale)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-navy-500">Time</span>
            <span className="font-mono font-medium" data-testid="locale-preview-time">
              {formatTime(sampleDate, previewLocale)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-navy-500">Currency</span>
            <span className="font-mono font-medium" data-testid="locale-preview-currency">
              {formatCurrency(2900, sampleCurrency, previewLocale)}
            </span>
          </div>
        </div>

        {/* Phase C3 — Track A — server-rendered preview. Exposes the
            Babel-backed formatter so the operator sees the EXACT date /
            currency that will appear in their next outgoing email / PDF /
            CSV — not the browser's `Intl.*` approximation. */}
        <div
          className="mt-3 p-4 bg-orange-50/40 border border-orange-100 rounded-lg space-y-1.5 text-sm"
          data-testid={`locale-server-preview-${scope}`}
        >
          <div className="text-[10px] uppercase tracking-wider text-orange-700 font-semibold mb-2 flex items-center justify-between">
            <span>Outgoing emails &amp; PDFs (server)</span>
            {serverPreviewLoading && <span className="text-navy-400">…</span>}
          </div>
          {serverPreview ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-navy-500">Date</span>
                <span className="font-mono font-medium" data-testid="server-preview-date">
                  {serverPreview.date_short}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-navy-500">Date &amp; time</span>
                <span className="font-mono font-medium" data-testid="server-preview-datetime">
                  {serverPreview.datetime_short}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-navy-500">£29 plan price</span>
                <span className="font-mono font-medium" data-testid="server-preview-currency-gbp">
                  {serverPreview.currency_gbp}
                </span>
              </div>
            </>
          ) : (
            <div className="text-xs text-navy-400 italic">Preview unavailable — saving still works.</div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 pt-2">
          <p className="text-[11px] text-navy-400 italic flex-1">
            {scope === "org"
              ? "Individual users can override this from their profile."
              : "Affects only your own view; org-wide emails / PDFs still use the org locale."}
          </p>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid={`locale-save-btn-${scope}`}
          >
            {saving ? "Saving…" : "Save locale"}
          </Button>
        </div>
      </div>
    </div>
  );
}
